import React, { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Sikka IT Solutions';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center">
              <img src={require('../assets/sikka-logo.jpg')} alt="Sikka IT Solutions" className="h-12 w-auto object-contain" />
            </a>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = '/'}>
              Back to Home <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16 max-w-4xl">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-10">Last updated: March 16, 2026</p>

        <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">1. Introduction</h2>
            <p>Sikka IT Solutions ("Company", "we", "us", or "our"), located at Sikka House, 6 La Place, Hazratganj, Lucknow - 226001, is committed to protecting the privacy and security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you visit our website (www.sikkait.com) or engage our services.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">2. Information We Collect</h2>
            <p><strong>Personal Information:</strong> When you fill out our contact form, career application, or engage our services, we may collect your name, email address, phone number, company name, job title, and project requirements.</p>
            <p className="mt-2"><strong>Usage Data:</strong> We automatically collect certain information when you visit our website, including your IP address, browser type, operating system, referring URLs, pages viewed, and the date and time of your visit.</p>
            <p className="mt-2"><strong>Cookies:</strong> Our website may use cookies and similar tracking technologies to enhance your browsing experience and analyze website traffic.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>To respond to your inquiries and provide requested services</li>
              <li>To process job applications submitted through our careers page</li>
              <li>To communicate project updates, proposals, and invoices</li>
              <li>To improve our website, services, and user experience</li>
              <li>To send relevant business communications (with your consent)</li>
              <li>To comply with legal obligations and protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">4. Data Sharing & Disclosure</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your data only in the following circumstances:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Service Providers:</strong> With trusted third-party vendors who assist in operating our website and delivering services (e.g., email services, hosting), bound by confidentiality agreements</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
              <li><strong>Business Protection:</strong> To protect the rights, property, or safety of Sikka IT Solutions, our clients, or others</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">5. Data Security</h2>
            <p>We implement industry-standard security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These include encrypted data transmission (SSL/TLS), secure server infrastructure, access controls, and regular security assessments. However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">6. Data Retention</h2>
            <p>We retain your personal information only for as long as necessary to fulfil the purposes outlined in this policy, comply with legal obligations, resolve disputes, and enforce our agreements. Contact form submissions and career applications are retained for a period of 24 months unless a longer retention period is required by law.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate or incomplete data</li>
              <li>Request deletion of your personal data (subject to legal obligations)</li>
              <li>Withdraw consent for marketing communications at any time</li>
              <li>Lodge a complaint with the relevant data protection authority</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, please contact us at raunak@sikkanet.com.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">8. Third-Party Links</h2>
            <p>Our website may contain links to third-party websites (e.g., Sikka Broadband, Sikka Infratech). We are not responsible for the privacy practices or content of these external sites. We encourage you to review their privacy policies before providing any personal information.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">9. Children's Privacy</h2>
            <p>Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected data from a minor, we will take steps to delete such information promptly.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">10. Changes to This Policy</h2>
            <p>We reserve the right to update this Privacy Policy at any time. Changes will be posted on this page with an updated revision date. Continued use of our website after changes constitutes acceptance of the revised policy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">11. Contact Us</h2>
            <p>For any questions or concerns about this Privacy Policy or our data practices, please contact:</p>
            <p className="mt-2">
              <strong>Sikka IT Solutions</strong><br />
              Sikka House, 6 La Place, Hazratganj, Lucknow - 226001<br />
              Email: raunak@sikkanet.com
            </p>
          </section>
        </div>
      </main>

      <footer className="bg-slate-950 text-slate-400 text-sm text-center py-6">
        <p>&copy; 2026 Sikka IT Solutions. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
